import { Link } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  SimilarWorkOrder,
  similarWorkOrdersQuery,
} from "@/app/ProDrivers/WorkOrders/GraphQL/similarWorkOrdersQuery";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { formatDate } from "@/lib/Formatters/formatDate";

export function WorkOrderSimilarNotes({
  workOrderId,
}: {
  workOrderId: string;
}) {
  const t = useTranslations("workOrder.details");
  const { data } = useSuspenseGqlQuery(similarWorkOrdersQuery, {
    work_order_id: workOrderId,
    first: 5,
  });
  const similarWorkOrders = data.similarWorkOrders.data;

  return (
    <div className="space-y-2">
      {similarWorkOrders.length > 0 ? (
        <ul className="max-h-64 space-y-2 overflow-y-auto">
          {similarWorkOrders.map((similar) => (
            <SimilarWorkOrderNote key={similar.id} workOrder={similar} />
          ))}
        </ul>
      ) : (
        <p className="rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-500">
          {t("noSimilarWorkOrderNotes")}
        </p>
      )}
    </div>
  );
}

function SimilarWorkOrderNote({ workOrder }: { workOrder: SimilarWorkOrder }) {
  const t = useTranslations("workOrder.details");
  const tVehicle = useTranslations("vehicle");
  const quote = workOrder.currentQuote;

  return (
    <li className="rounded-lg border border-gray-200 bg-white px-3 py-2">
      <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
        <Link
          from="/$tenant/$orgId/supplier/{-$supplierId}"
          to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id"
          params={(prev) => ({
            ...prev,
            id: workOrder.id,
          })}
          className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
        >
          {workOrder.reference}
        </Link>
        <span className="truncate">
          {workOrder.departureAddress.city ?? "--"} →{" "}
          {workOrder.deliveryAddress.city ?? "--"}
        </span>
        <span className="shrink-0">{formatDate(workOrder.depart_after)}</span>
      </div>
      <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
        <span>{tVehicle(`type.${workOrder.vehicle_type}`)}</span>
        <span>× {workOrder.number_of_drivers}</span>
        {workOrder.has_return_trip ? <span>{t("returnTrip")}</span> : null}
        {quote ? (
          <span className="ml-auto font-medium text-gray-700">
            {formatCurrency(quote.amount_exc_tax, quote.currency)}
          </span>
        ) : null}
      </div>
      <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">
        {workOrder.admin_notes}
      </p>
    </li>
  );
}
